import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * One hidden <audio> per person on the call.
 *
 * These live in CallProvider rather than in the call window, and that placement
 * is the whole reason the component exists: CallWindow unmounts when the call
 * is minimised, so a sink rendered inside it would take the sound with it —
 * minimising a call would be indistinguishable from muting everybody.
 *
 * One element per participant rather than one mixed stream. Mixing would mean a
 * Web Audio graph in the path of every call, and the browser's own mixing is
 * free, already handles a participant leaving mid-sentence, and keeps each
 * person's audio independently controllable if per-person volume is ever wanted.
 */

const RemoteAudio = ({ stream, onBlocked, sinkId }) => {
    const ref = useRef(null);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        let active = true;
        el.srcObject = stream || null;
        if (stream) el.play().catch(() => { if (active) onBlocked(); });
        return () => {
            active = false;
            el.srcObject = null;
        };
    }, [stream, onBlocked]);

    // Empty sinkId means "leave it on whatever the browser already defaults
    // to" — setSinkId is only called once somebody has actually chosen a
    // device, not on every element as it mounts.
    useEffect(() => {
        const el = ref.current;
        if (!el?.setSinkId || !sinkId) return;
        el.setSinkId(sinkId).catch(() => { /* device unplugged since — leave the current one */ });
    }, [sinkId]);

    return <audio ref={ref} autoPlay playsInline />;
};

const CallAudioSinks = ({ media, sinkId }) => {
    const ref = useRef(null);
    const [blocked, setBlocked] = useState(false);
    const onBlocked = useCallback(() => setBlocked(true), []);
    const hasAudio = Object.values(media || {}).some((tracks) => tracks?.audio || tracks?.screenAudio);

    useEffect(() => { if (!hasAudio) setBlocked(false); }, [hasAudio]);

    const retryPlayback = () => {
        setBlocked(false);
        // Call play directly inside the click gesture, for every participant.
        ref.current?.querySelectorAll('audio').forEach((el) => {
            if (el.srcObject) el.play().catch(onBlocked);
        });
    };

    return (
        <>
            <div ref={ref} className="call-audio-sinks" aria-hidden="true">
                {Object.entries(media || {}).map(([userId, tracks]) => (
                    <React.Fragment key={userId}>
                        {tracks?.audio && <RemoteAudio stream={tracks.audio} onBlocked={onBlocked} sinkId={sinkId} />}
                        {tracks?.screenAudio && <RemoteAudio stream={tracks.screenAudio} onBlocked={onBlocked} sinkId={sinkId} />}
                    </React.Fragment>
                ))}
            </div>
            {blocked && hasAudio && (
                <button type="button" className="call-audio-retry" onClick={retryPlayback}>
                    Enable call audio
                </button>
            )}
        </>
    );
};

export default CallAudioSinks;
